import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { DrupalRESTService } from './drupal-rest.service';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';

export interface TaxonomyOverride {
  color: string | null;
  past_event_color: string | null;
  hide: boolean;
  weight: number | null;
}

export type TaxonomyOverridesMap = { [termId: string]: TaxonomyOverride };

@Injectable({
  providedIn: 'root'
})
export class TaxonomyOverridesService {
  private overrides = new BehaviorSubject<TaxonomyOverridesMap>({});
  private loaded = false;
  private isLoading = false;
  private currentStudioId: string = '';

  constructor(
    private drupalRESTService: DrupalRESTService,
    private authService: AuthService
  ) {
    this.authService.selectedStudioChanged.subscribe((studio) => {
      const newStudioId = studio?.id?.toString() || '';
      if (newStudioId !== this.currentStudioId) {
        this.currentStudioId = newStudioId;
        this.refresh();
      }
    });
  }

  getOverrides(): Observable<TaxonomyOverridesMap> {
    if (!this.loaded && !this.isLoading && this.hasStudioContext()) {
      this.load();
    }
    return this.overrides.asObservable().pipe(
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    );
  }

  getOverridesSnapshot(): TaxonomyOverridesMap {
    return this.overrides.value;
  }

  public refresh() {
    this.loaded = false;
    this.overrides.next({});
    if (this.hasStudioContext()) {
      this.load();
    }
  }

  private load() {
    if (this.isLoading) return;
    if (!this.hasStudioContext()) return;

    this.isLoading = true;
    const configFieldName = 'field_taxonomy_overrides_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response: any) => {
          this.overrides.next(this.normalize(response));
          this.loaded = true;
          this.isLoading = false;
        },
        (error) => {
          LoggerService.error('Error loading taxonomy overrides:', error);
          this.overrides.next({});
          this.loaded = true;
          this.isLoading = false;
        }
      );
  }

  private normalize(response: any): TaxonomyOverridesMap {
    if (response === null || response === undefined) return {};

    let list: any[];
    if (Array.isArray(response)) {
      list = response;
    } else if (typeof response === 'object') {
      // Backend returns a single object when the studio has exactly one override
      // (loadStudioAndConfigField operator-precedence quirk in amt_general.module).
      list = [response];
    } else {
      return {};
    }

    const map: TaxonomyOverridesMap = {};
    for (const item of list) {
      const termId = item?.field_term_id?.[0]?.value;
      if (!termId) continue;
      const rawWeight = item?.field_weight?.[0]?.value;
      map[String(termId)] = {
        color: item?.field_color?.[0]?.value ?? null,
        past_event_color: item?.field_past_event_color?.[0]?.value ?? null,
        hide: item?.field_hide?.[0]?.value === '1' || item?.field_hide?.[0]?.value === true,
        weight: rawWeight === null || rawWeight === undefined || rawWeight === '' ? null : Number(rawWeight),
      };
    }
    return map;
  }

  private hasStudioContext(): boolean {
    return !!(
      this.authService.selectedStudio?.id
      || this.authService.studios?.[0]?.id
    );
  }
}
